import { useEffect, useState, useRef } from "react";
import { Column, Row, TextCell } from "@silevis/reactgrid";
import * as XLSX from "xlsx";
import { CustomCell } from "./Cells/CustomCellTemplate";
import { IndexCell } from "./Cells/IndexCellTemplate";
import { fetchFileToFileAndBlob } from "@lhd/utility";

export interface IProps {
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  uri?: string;
}
type CustomRow = Row<TextCell | CustomCell | IndexCell>;

const useExcel = (props: IProps) => {
  const { uri, setLoading } = props;
  const [rows, setRows] = useState<CustomRow[]>([]);
  const [columns, setColumns] = useState<Column[]>([]);
  const refFile = useRef<Awaited<ReturnType<typeof fetchFileToFileAndBlob>>>();

  const [sheets, setSheets] = useState<
    { sheetName: string; active: boolean }[]
  >([]);
  const isCSV = !!uri?.toLowerCase().endsWith(".csv");

  const refContainer = useRef<HTMLDivElement>(null);

  async function excelToCustomRows(file: Blob | File): Promise<CustomRow[]> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (e: ProgressEvent<FileReader>) => {
        try {
          const data = e.target?.result;
          const workbook = isCSV
            ? XLSX.read(data, { type: "string", raw: true })
            : XLSX.read(new Uint8Array(data as ArrayBuffer), { type: "array" });

          let sheetName = workbook.SheetNames[0];
          if (sheets?.length) {
            sheetName = sheets.find((x) => x.active)?.sheetName || sheetName;
          } else {
            const newSheets = workbook.SheetNames.map((sheetName, i) => ({
              sheetName,
              active: i === 0,
            }));
            setSheets(newSheets);
          }

          const worksheet = workbook.Sheets[sheetName];
          const range = XLSX.utils.decode_range(worksheet["!ref"] || "");
          if (range.s.r === -1 || range.e.c === -1) {
            resolve([]);
            return;
          }

          const rowHeader = {
            rowId: "header",
            cells: [] as TextCell[],
          };

          const dataRows: CustomRow[] = [];

          for (let rowNum = range.s.r; rowNum <= range.e.r; rowNum++) {
            const rowData: CustomCell[] = [];
            const cellHeaders = [{ text: "", type: "text" }] as TextCell[];

            for (let colNum = range.s.c; colNum <= range.e.c; colNum++) {
              const cellAddress = XLSX.utils.encode_cell({
                r: rowNum,
                c: colNum,
              });
              const cell = worksheet[cellAddress];
              const colLetter = XLSX.utils.encode_col(colNum);
              const text = cell ? cell.w || cell.v || "" : "";

              // !rowHeader.cells.length = it is first row
              if (!rowHeader.cells.length) {
                cellHeaders.push({
                  text: isCSV ? text : colLetter,
                  type: "text",
                  style: {
                    background: "#a5a5a5",
                  },
                });
              }

              if (rowHeader.cells.length || !isCSV) {
                rowData.push({
                  text: text,
                  type: "custom",
                });
              }
            }

            if (!rowHeader.cells.length) rowHeader.cells = cellHeaders;

            rowData.length &&
              dataRows.push({
                rowId: isCSV ? rowNum : rowNum + 1,
                cells: [
                  {
                    text: String(isCSV ? rowNum : rowNum + 1),
                    type: "index",
                  },
                  ...rowData,
                ],
              });
          }

          resolve([rowHeader, ...dataRows]);
        } catch (err) {
          console.log(err);
          reject(err);
        }
      };

      reader.onerror = (err) => reject(err);

      if (isCSV) {
        reader.readAsText(file);
      } else {
        reader.readAsArrayBuffer(file);
      }
    });
  }

  async function handleSetData(uri: string) {
    const dataFile = refFile.current || (await fetchFileToFileAndBlob(uri));
    refFile.current = dataFile;
    const [file] = dataFile;
    const rows = await excelToCustomRows(file);
    setRows(rows);
    setLoading(false);
  }

  const handleColumnResized = (colId: string | number, newWidth: number) => {
    setColumns((prev) =>
      prev.map((col) =>
        col.columnId === colId ? { ...col, width: newWidth } : col,
      ),
    );
  };

  const handleChooseSheet = (sheetName: string) => {
    setLoading(true);
    const newSheets = sheets.map((sheet) => ({
      ...sheet,
      active: sheet.sheetName === sheetName,
    }));
    setSheets(newSheets);
  };

  useEffect(() => {
    uri && handleSetData(uri);
  }, [uri, sheets]);

  useEffect(() => {
    const rowHeader = rows.find((x) => x.rowId === "header");
    if (!rowHeader) {
      setColumns([]);
    } else {
      const cols = rowHeader.cells.map((cell, index) => ({
        columnId: index === 0 ? "index" : (cell as TextCell).text,
        width: index === 0 ? 25 : 100, // index = 0 =>  col is index col
        resizable: true,
      }));
      setColumns(cols);
    }
  }, [rows]);

  return {
    ...props,
    refContainer,
    columns,
    rows,
    handleColumnResized,
    handleChooseSheet,
    sheets,
    isCSV,
  };
};

export type Props = ReturnType<typeof useExcel>;

export default useExcel;
